import { Slideout } from "@arelia-components/ui-atoms/Slideout";
import { ChatBubbleOutlineOutlined } from "@mui/icons-material";
import { Box, Button, Paper, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { ChatLine, ChatRole, useAiChatMutation } from "src/api/ai";
import { toast } from "sonner";
import theme from "src/theme";
import { useScrollToBottom } from "../../hooks/useScrollToBottom";
import { ChatMessage } from "./ChatMessage";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply?: (caption: string) => void;
  systemPromptOverride?: string;
  initialGreeting?: string;
  inputPlaceholder?: string;
};

export function ChatSlideout({
  open,
  onOpenChange,
  onApply,
  systemPromptOverride,
  initialGreeting,
  inputPlaceholder,
}: Props) {
  const setOpen = (v: boolean) => onOpenChange(v);

  const [lines, setLines] = useState<ChatLine[]>([]);
  const [message, setMessage] = useState("");
  const linesRef = useRef<HTMLTextAreaElement>(null);
  const aiChat = useAiChatMutation({
    onSuccess: (data) => setLines(data),
    onError: () => { toast.error("Something went wrong. Try to change the prompt or try later."); },
  });

  useEffect(() => {
    if (initialGreeting) {
      setLines([{ role: ChatRole.Assistant, content: initialGreeting }]);
    }
  }, [initialGreeting]);
  /*Automatically scroll to bottom when new messages are added*/
  useScrollToBottom(linesRef, [lines]);

  const send = () => {
    const systemLines: ChatLine[] = systemPromptOverride ? [{ role: ChatRole.System, content: systemPromptOverride }] : [];
    const newLines: ChatLine[] = [...systemLines, ...lines, { role: ChatRole.User, content: message }];
    aiChat.mutate({ messages: newLines, jsonMode: !!onApply });
    setLines(newLines);
    setMessage("");
  }

  /*To send message by pressing Enter key*/
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (message.trim()) send();
    }
  };

  return <>
    <Slideout title="Ask ARELIA" open={open} setOpen={setOpen} icon={<ChatBubbleOutlineOutlined color="success" />}>
      <Box ref={linesRef} sx={styles.messagesContainer}>
        {lines.filter(l => l.role !== ChatRole.System).map((line, idx) => (
          <ChatMessage
            key={idx}
            line={line}
            onApply={onApply}
            onClose={() => setOpen(false)}
          />
        ))}
        {aiChat.isLoading && (
          <Box sx={styles.assistantRow}>
            <Paper elevation={0} sx={styles.loadingBubble}>
              <Typography variant="body2" color="text.secondary" fontStyle="italic">Thinking...</Typography>
            </Paper>
          </Box>
        )}
      </Box>
      <Box sx={styles.inputContainer}>
        <TextField
          sx={styles.inputField}
          placeholder={inputPlaceholder ?? "Ask me anything..."}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={aiChat.isLoading}
        ></TextField>
        <Button variant="contained" onClick={() => send()} disabled={!message.trim() || aiChat.isLoading}>
          SEND
        </Button>
      </Box>

    </Slideout >
  </>;
}

export const styles = {
  messagesContainer: {
    display: "flex",
    padding: "30px",
    flexDirection: "column",
    flexGrow: 1,
    overflowX: "auto",
  },
  assistantRow: {
    display: "flex",
    mb: 1.5,
  },
  assistantBubble: {
    p: 2,
    mr: 5,
    backgroundColor: "grey.100",
    borderRadius: "4px 16px 16px 16px",
    color: "text.primary",
  },
  assistantLabel: {
    fontWeight: 700,
    color: "text.secondary",
    mb: 0.5,
    display: "block",
  },
  messageText: {
    whiteSpace: "pre-wrap",
    fontSize: "0.875rem",
  },
  optionCard: {
    mt: 1.5,
    p: 1.5,
    backgroundColor: "white",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
  },
  optionLabel: {
    color: "text.secondary",
    fontWeight: 600,
  },
  optionText: {
    mt: 0.5,
    whiteSpace: "pre-wrap",
    fontSize: "0.875rem",
  },
  applyButton: {
    mt: 1,
  },
  userRow: {
    display: "flex",
    justifyContent: "flex-end",
    mb: 1.5,
  },
  userBubble: {
    p: 1.5,
    ml: 5,
    textAlign: "right",
    backgroundColor: theme.palette.primary.main,
    color: "white",
    borderRadius: "16px 4px 16px 16px",
    fontSize: "0.875rem",
  },
  loadingBubble: {
    p: 2,
    mr: 5,
    backgroundColor: "grey.100",
    borderRadius: "4px 16px 16px 16px",
  },
  inputContainer: {
    display: "flex",
    padding: "30px",
    justifyContent: "flex-end",
  },
  inputField: {
    flexGrow: 1,
    mr: 2,
  },
};

